import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * AccessGrant : famille "autorisation d'accès nominative et datée".
 *
 * Autorisation nominative et datée d'accéder à un lieu, un équipement ou un
 * service (physique ou numérique), activable et révocable. Construite sur le
 * modèle "famille + type" de la bible (patron : `asset`).
 *
 * Le socle ne connaît pas l'accès concret ("badge salle", "code portail",
 * "clé casier") : il connaît la famille générique `accessGrant`. Le vocabulaire
 * métier est porté par le champ `type` (chaîne libre déclarée par l'app).
 *
 * Nominative : `granteeContactId` (le bénéficiaire, référence `contact`) est le
 * CŒUR de la famille, donc requis (comme subscription.contactId). La CIBLE est
 * polymorphe, en références optionnelles (aucun import de schéma) : `placeId`
 * (un lieu), `resourceId` (un équipement) ou `portalRef` (un service numérique,
 * référence externe libre) ; aucune n'est seule requise. La fenêtre de validité
 * est portée par `validFrom` / `validUntil`, la révocation par `revokedAt`
 * (vide tant que l'autorisation court).
 *
 * Autres liens = de simples ids optionnels : `subscriptionId` (l'engagement
 * dont dérive l'accès, référence `subscription`), `loanId` (le prêt dont dérive
 * l'accès, référence `loan`). Les passages ne vivent PAS ici : chaque passage
 * est un `attendance` qui porte l'`accessGrantId` (le lien est côté attendance).
 *
 * Sécurité : owner + rôle gestion des accès (hors v0). Le `credentialCode` (le
 * secret qui ouvre : code, empreinte de badge) N'EST PAS dans ce document v0 :
 * il ira dans un sous-document restreint ou sous forme de hash, jamais listable
 * en collection (cf. commentaire dans les coreFields). En v0 la famille reste
 * org-wide (même doctrine qu'asset).
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille accessGrant (hors champs standard du socle). */
export const accessGrantCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "badge", "code", "cle", "acces_numerique"). */
  type: z.string().min(1),
  /** Bénéficiaire nommé de l'autorisation (référence `contact`, cœur de la famille). Requis. */
  granteeContactId: z.string().min(1),
  /** Cible = lieu (référence `place`) ; cible polymorphe, optionnel. */
  placeId: z.string().min(1).optional(),
  /** Cible = équipement (référence `resource`) ; cible polymorphe, optionnel. */
  resourceId: z.string().min(1).optional(),
  /** Cible = service numérique / portail, référence externe libre ; cible polymorphe, optionnel. */
  portalRef: z.string().min(1).optional(),
  /** Début de validité de l'autorisation. Optionnel. */
  validFrom: z.date().optional(),
  /** Fin de validité de l'autorisation (vide tant qu'elle court). Optionnel. */
  validUntil: z.date().optional(),
  /** Date de révocation (vide tant que l'autorisation est active). Optionnel. */
  revokedAt: z.date().optional(),
  /** Engagement dont dérive l'accès (référence `subscription`), optionnel. */
  subscriptionId: z.string().min(1).optional(),
  /** Prêt dont dérive l'accès (référence `loan`), optionnel. */
  loanId: z.string().min(1).optional(),
  // credentialCode : sensible, ira dans un sous-document prive ou un hash (cf. WIP), hors v0 ; jamais listable en collection.
} as const;

/** Schéma complet de la famille accessGrant (champs standard + champs métier). */
export const AccessGrantSchema = z.object({
  ...baseEntityFields,
  ...accessGrantCoreFields,
});

/** Type d'un accessGrant. */
export type AccessGrant = z.infer<typeof AccessGrantSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const AccessGrantCreateInputSchema = z.object({
  ...accessGrantCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'un accessGrant. */
export type AccessGrantCreateInput = z.infer<typeof AccessGrantCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const AccessGrantUpdateInputSchema = z
  .object({ ...accessGrantCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'un accessGrant. */
export type AccessGrantUpdateInput = z.infer<typeof AccessGrantUpdateInputSchema>;
